import type { MouseEventHandler } from 'react';
// mantine
import { Avatar, UnstyledButton } from '@mantine/core';
// components
import { AudioPlayerControl, CopyButton } from '@newo/ui-kit';
import { getRecordingName } from '@newo/toolkit/bff';
import type { IAct } from '@newo/api/gateway';
import Delta from './components/Delta/Delta.tsx';
import DateTime from '@/modules/DateTime/components/DateTime';
// utils
import { DATE_FORMAT } from '@/const';
import { cn } from '@/utils';
import { getIntegrationLogo } from '@/modules/Integrations/Integrations.utils';
// assets
import styles from '@/modules/Chat/components/ConversationsEvent/ConversationsEvent.module.css';

interface ConversationsEventProps {
    className?: string;
    act: IAct;
    onDetails?: MouseEventHandler<HTMLButtonElement>;
    onReport?: MouseEventHandler<HTMLButtonElement>;
    handleSelected?: () => void;
    isSelected?: boolean;
    selectedAct?: IAct;
}

const ConversationsEvent = ({
    className,
    act,
    onDetails,
    onReport,
    handleSelected,
    selectedAct
}: ConversationsEventProps) => {
    const summary = act.arguments.find((arg) => arg.name === 'summary')?.value;

    const withPlayer =
        act.recordings.length > 0 && act.reference_idn === 'conversation_ended';

    const isSelected = act.id === selectedAct?.id;

    return (
        <div
            className={cn(
                'grid [grid-template-columns:1fr_30px] relative items-center gap-[5px]',
                {
                    [styles.contentContainer]: !isSelected
                }
            )}
        >
            <UnstyledButton
                className={cn(styles.event, className, {
                    [styles.selected]: isSelected
                })}
                onClick={handleSelected}
            >
                <div className={styles.top}>
                    <Avatar
                        alt="vapi"
                        className="bg-white"
                        radius="xl"
                        size="xs"
                        src={getIntegrationLogo(act.integration_idn ?? '')}
                        title={act.integration_idn ?? ''}
                    />
                    <span
                        className={cn('mb-0.5 pr-4 font-medium', {
                            [styles.isSelected]: isSelected
                        })}
                    >
                        {act.reference_idn}
                    </span>
                </div>
                {summary ? (
                    <div
                        className={cn(styles.summaryWrapper, {
                            [styles.isSummarySelected]: isSelected
                        })}
                    >
                        <div
                            className={cn(
                                'flex items-center gap-2 text-gray-6',
                                { [styles.isSummarySelected]: isSelected }
                            )}
                        >
                            Summary
                            <CopyButton
                                className="[&>svg]:w-3.5 [&>svg]:h-3.5"
                                value={summary}
                            />
                        </div>
                        <div
                            className={cn(styles.summary, {
                                'text-[white]': isSelected
                            })}
                        >
                            {summary}
                        </div>
                    </div>
                ) : undefined}

                {withPlayer ? (
                    <div
                        className={cn(styles.playerSection, {
                            [styles.isSelectedPlayer]: isSelected
                        })}
                    >
                        {act.recordings.map((recording, index) => (
                            <AudioPlayerControl
                                className={cn(
                                    'text-black-6 text-[10px]',
                                    styles.playerButton,
                                    { [styles.isButtonSelected]: isSelected }
                                )}
                                key={recording.play_url}
                                label={getRecordingName(
                                    `Recording #${index + 1}`,
                                    recording.duration
                                )}
                                size="xs"
                                trackName={act.external_id ?? ''}
                                trackUrl={recording.play_url}
                                type="button"
                            />
                        ))}
                    </div>
                ) : undefined}

                <div className="flex items-center gap-4">
                    <UnstyledButton
                        className={cn(
                            'text-black-6 text-[10px]',
                            styles.reportButton,
                            { [styles.isSelected]: isSelected }
                        )}
                        onClick={onDetails}
                    >
                        Show details
                    </UnstyledButton>
                    <UnstyledButton
                        className={cn(
                            'text-black-6 text-[10px]',
                            styles.reportButton,
                            { [styles.isSelected]: isSelected }
                        )}
                        onClick={onReport}
                    >
                        Report
                    </UnstyledButton>
                    <div
                        className={cn(
                            'ml-auto text-xs pl-4  text-gray-6 text-[10px]',
                            {
                                [styles.isSelected]: isSelected,
                                [styles.dateTime]: !isSelected
                            }
                        )}
                    >
                        <DateTime
                            format={DATE_FORMAT.FULL_12H_MS}
                            value={act.datetime}
                        />
                    </div>
                </div>
            </UnstyledButton>
            <Delta
                baseIso={selectedAct?.datetime}
                className={styles.delta}
                targetIso={act.datetime}
            />
        </div>
    );
};

export default ConversationsEvent;
